import { computeQuality, qualityBand, qualityColorClasses, coverageMetricLabel } from '@/lib/qualityScore';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * QualityBreakdownPopover — click-to-open detailed breakdown of the
 * Dividend Quality Score. Surfaces every contributing pillar, every
 * fundamental-derived bonus (ROE, revenue stability, dividend policy),
 * and every structural penalty (leveraged, CEF, debt, dividend cut).
 *
 * Use this anywhere the user might want to gut-check a score before
 * acting on it (Portfolio rows, Stock Modal header, Screener results).
 *
 * Props:
 *   • stock        — full stock-cache record
 *   • children     — the trigger element (typically a QualityBadge)
 *   • align        — radix popover align ('start' | 'center' | 'end')
 *   • side         — radix popover side ('top' | 'bottom' | 'left' | 'right')
 */
function fmtPct(value, digits = 1) {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

function PillarBar({ label, value, max, accent = 'sky' }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (Math.max(0, value) / max) * 100)) : 0;
  const isNegative = value < 0;
  const accentBg = {
    sky:     'bg-sky-500',
    emerald: 'bg-emerald-500',
    amber:   'bg-amber-500',
    violet:  'bg-violet-500',
    fuchsia: 'bg-fuchsia-500',
  }[accent] || 'bg-sky-500';
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="font-semibold">{label}</span>
        <span className={`font-mono ${isNegative ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground'}`}>
          {value > 0 ? '+' : ''}{value} / {max}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-muted/60 overflow-hidden">
        <div
          className={`h-full rounded-full ${isNegative ? 'bg-rose-500' : accentBg}`}
          style={{ width: isNegative ? '100%' : `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function QualityBreakdownBody({ stock }) {
  const quality = computeQuality(stock);
  const band = qualityBand(quality?.score);
  const cls = qualityColorClasses(band.tone);
  const ticker = stock?.ticker || '';
  if (!quality) {
    return (
      <p className="text-xs text-muted-foreground">
        Not enough data on file to score <strong>{ticker}</strong> fairly.
        Live-refresh the ticker to populate it.
      </p>
    );
  }
  const { breakdown, penalty, penaltyReasons, fundamentalReasons, isFund, isEtf, fundCategory, forward, partial, trailingCore } = quality;
  return (
    <>
    {/* Header — score + band */}
    <div className="flex items-center justify-between gap-3 mb-3 pb-3 border-b">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
          {isFund ? `Quality Score · ${isEtf ? 'ETF' : 'Fund'} pathway` : 'Quality Score'}
        </div>
        <div className="text-2xl font-extrabold leading-none mt-0.5" data-testid={`quality-popover-score-${ticker}`}>
          {quality.score}
          <span className="text-sm font-mono ml-1 text-muted-foreground">/{isFund ? '90' : '100'}</span>
          {(!isFund && partial) ? (
            <span className="ml-1.5 align-middle inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 ring-1 ring-amber-400/40 px-1.5 py-px text-[9px] font-bold uppercase tracking-wider">
              Partial
            </span>
          ) : null}
        </div>
        {isFund && fundCategory ? (
          <div className="text-[10px] text-muted-foreground mt-0.5">{fundCategory}</div>
        ) : null}
      </div>
      <span
        className={`inline-flex items-center font-bold uppercase tracking-wider ring-1 rounded-full px-2 py-0.5 text-[11px] gap-1 ${cls.bg} ${cls.text} ${cls.ring}`}
      >
        <span className={`rounded-full w-2 h-2 ${cls.dot}`} />
        {band.short} · {band.label}
      </span>
    </div>

    {/* Pillars — different rubric for funds vs. corporate stocks */}
    {isFund ? (
      <div className="space-y-2.5">
        <PillarBar label="Yield stability (vs 3yr avg)" value={breakdown.yield}     max={30} accent="sky" />
        <PillarBar label="Distribution growth"         value={breakdown.growth}    max={25} accent="violet" />
        <PillarBar label="Longevity (streak/tier)"     value={breakdown.longevity} max={25} accent="amber" />
        <PillarBar label="Asset-class confidence"      value={breakdown.confidence || 0} max={20} accent="emerald" />
      </div>
    ) : (
      <div className="space-y-2.5">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
          <span>Trailing core</span>
          <span className="font-mono normal-case">{trailingCore ?? '—'} / 60</span>
        </div>
        <PillarBar label="Safety (payout ratio)"   value={breakdown.safety}    max={30} accent="emerald" />
        <PillarBar label="Yield"                   value={breakdown.yield}     max={25} accent="sky" />
        <PillarBar label="Growth (5-yr CAGR)"      value={breakdown.growth}    max={20} accent="violet" />
        <PillarBar label="Longevity (streak/tier)" value={breakdown.longevity} max={25} accent="amber" />
      </div>
    )}

    {/* Forward signals — SEC XBRL coverage (June 2026 overhaul) */}
    {forward ? (
      <div className="mt-3 pt-3 border-t" data-testid={`quality-forward-section-${ticker}`}>
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider font-bold text-sky-700 dark:text-sky-400 mb-1.5">
          <span>Forward signals · SEC filings</span>
          <span className="font-mono normal-case">+{Math.round(forward.total)} / 40</span>
        </div>
        <div className="space-y-2.5">
          <PillarBar
            label={`${coverageMetricLabel(forward.metric)} · ${forward.ratio.toFixed(2)}× TTM`}
            value={Math.round(forward.coveragePts)}
            max={25}
            accent="sky"
          />
          <PillarBar
            label={`Coverage trend${forward.trendPct != null ? ` · ${forward.trendPct > 0 ? '+' : ''}${forward.trendPct.toFixed(1)}%` : ' · n/a'}`}
            value={Math.round(forward.trendPts)}
            max={15}
            accent="fuchsia"
          />
        </div>
        {forward.latestQuarterEnd && (
          <p className="text-[10px] text-muted-foreground mt-1.5">
            Latest quarter: {forward.latestQuarterEnd} · {forward.metric} vs cash dividends paid
          </p>
        )}
      </div>
    ) : (!isFund && partial) ? (
      <div
        className="mt-3 pt-3 border-t"
        data-testid={`quality-partial-note-${ticker}`}
      >
        <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 ring-1 ring-amber-300/40 px-2.5 py-2">
          <p className="text-[10px] leading-snug text-amber-800 dark:text-amber-300">
            <strong>Partial score (max 60).</strong> Forward coverage data from SEC
            filings isn't on file for this ticker yet — the score reflects trailing
            pillars only and upgrades automatically once coverage data lands.
          </p>
        </div>
      </div>
    ) : null}

    {/* Fundamentals (bonuses) — only for corporate stocks */}
    {!isFund && fundamentalReasons && fundamentalReasons.length > 0 && (
      <div className="mt-3 pt-3 border-t">
        <div className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-bold mb-1.5">
          Fundamental bonuses · +{breakdown.fundamentals || 0} pts
        </div>
        <ul className="space-y-1">
          {fundamentalReasons.map((r) => (
            <li key={r.key} className="flex items-center justify-between text-[11px]">
              <span className="text-muted-foreground">
                {r.label}
                {r.value != null && (
                  <span className="ml-1 font-mono opacity-70">
                    ({r.key === 'rev_stability' ? r.value.toFixed(3)
                      : r.key === 'roe' ? fmtPct(r.value)
                      : r.key === 'policy_hike' ? `+${fmtPct(r.value)}`
                      : r.value})
                  </span>
                )}
              </span>
              <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">+{r.delta}</span>
            </li>
          ))}
        </ul>
      </div>
    )}

    {/* Penalties */}
    {penaltyReasons && penaltyReasons.length > 0 && (
      <div className="mt-3 pt-3 border-t">
        <div className="text-[10px] uppercase tracking-wider text-rose-700 dark:text-rose-400 font-bold mb-1.5">
          Penalties · -{penalty} pts
        </div>
        <ul className="space-y-1">
          {penaltyReasons.map((r) => (
            <li key={r.key} className="flex items-center justify-between text-[11px]">
              <span className="text-muted-foreground">
                {r.label}
                {r.value != null && (
                  <span className="ml-1 font-mono opacity-70">
                    ({r.key === 'high_de' ? r.value.toFixed(2)
                      : r.key === 'policy_cut' ? fmtPct(r.value)
                      : r.value})
                  </span>
                )}
              </span>
              <span className="font-mono font-bold text-rose-600 dark:text-rose-400">{r.delta}</span>
            </li>
          ))}
        </ul>
      </div>
    )}

    {/* Footer */}
    {isFund ? (
      <p className="text-[10px] text-muted-foreground mt-3 pt-3 border-t leading-snug">
        Funds are graded by yield stability + asset class — corporate metrics (ROIC, FCF, payout ratio) don't apply. Score capped at 90 / 100; "A+ Elite" stays reserved for corporate stocks where we have the full operational picture.
      </p>
    ) : (
      <p className="text-[10px] text-muted-foreground mt-3 pt-3 border-t leading-snug">
        100 pts = 60 trailing core + 40 forward signals (SEC-reported {forward ? coverageMetricLabel(forward.metric) : 'distribution coverage'}). Higher = more attractive dividend profile. Read alongside the Income Risk badge.
      </p>
    )}
    </>
  );
}

export default function QualityBreakdownPopover({ stock, children, align = 'center', side = 'bottom' }) {
  const ticker = stock?.ticker || '';
  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align={align}
        side={side}
        collisionPadding={8}
        className="w-80 max-w-[92vw] p-4 z-[200] max-h-[70vh] overflow-y-auto overscroll-contain"
        data-testid={`quality-popover-${ticker}`}
      >
        <QualityBreakdownBody stock={stock} />
      </PopoverContent>
    </Popover>
  );
}
